/**
 * IndexedDB-backed SyncStore (FLASH-30, FR-7) — the browser persistence for
 * the offline study engine. The FLASH-17 spike's primary target was
 * SQLite-WASM/OPFS; IndexedDB is the spec'd fallback (FR-7.1) and ships first
 * because it needs no wasm in the page bundle — the OPFS upgrade slots in
 * behind the same SyncStore interface without touching the engine.
 *
 * Schema (db "flash-sync", v1):
 *   events   keyPath event_id, index by_card (card_id), index by_acked (acked01)
 *   anchors  keyPath card_id   (write-once enforced here AND in the engine)
 *   server_auth keyPath card_id (§7.1 server-adopted snapshots)
 *
 * This file runs only in browsers; tsconfig has no DOM lib, so the minimal
 * IndexedDB surface used here is declared locally. Engine logic is covered by
 * test/client-sync.test.ts via MemorySyncStore; this adapter is intentionally
 * thin enough to review line-by-line.
 */
import type { SyncStore, StoredReviewEvent } from "./sync-engine";
import type { CardStateSnapshot, FsrsParamsRow } from "../shared/types";

// ── Minimal IndexedDB typings (no DOM lib in tsconfig) ───────────────
interface IdbRequest<T = unknown> {
  result: T;
  error: unknown;
  onsuccess: ((this: unknown, ev: unknown) => void) | null;
  onerror: ((this: unknown, ev: unknown) => void) | null;
}
interface IdbIndex {
  getAll(query?: unknown): IdbRequest<unknown[]>;
}
interface IdbObjectStore {
  put(value: unknown): IdbRequest;
  get(key: unknown): IdbRequest<unknown>;
  getAll(): IdbRequest<unknown[]>;
  delete(key: unknown): IdbRequest;
  index(name: string): IdbIndex;
  createIndex(name: string, keyPath: string): unknown;
}
interface IdbTransaction {
  objectStore(name: string): IdbObjectStore;
  oncomplete: (() => void) | null;
  onerror: (() => void) | null;
  error: unknown;
}
interface IdbDatabase {
  transaction(stores: string | string[], mode: "readonly" | "readwrite"): IdbTransaction;
  objectStoreNames: { contains(name: string): boolean };
  createObjectStore(name: string, opts: { keyPath: string }): IdbObjectStore;
}
interface IdbOpenRequest extends IdbRequest<IdbDatabase> {
  onupgradeneeded: (() => void) | null;
}
declare const indexedDB: { open(name: string, version: number): IdbOpenRequest };

function req<T>(r: IdbRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error instanceof Error ? r.error : new Error(String(r.error)));
  });
}

/** Stored event row: acked as 0/1 so it can be indexed (IDB can't index booleans). */
type EventRow = Omit<StoredReviewEvent, "acked"> & { acked01: 0 | 1 };

function toRow(e: StoredReviewEvent): EventRow {
  const { acked, ...rest } = e;
  return { ...rest, acked01: acked ? 1 : 0 };
}
function fromRow(r: EventRow): StoredReviewEvent {
  const { acked01, ...rest } = r;
  return { ...rest, acked: acked01 === 1 };
}

export class IndexedDbSyncStore implements SyncStore {
  private constructor(private readonly db: IdbDatabase) {}

  static async open(name = "flash-sync"): Promise<IndexedDbSyncStore> {
    const openReq = indexedDB.open(name, 2);
    openReq.onupgradeneeded = () => {
      const db = openReq.result;
      if (!db.objectStoreNames.contains("events")) {
        const events = db.createObjectStore("events", { keyPath: "event_id" });
        events.createIndex("by_card", "card_id");
        events.createIndex("by_acked", "acked01");
      }
      if (!db.objectStoreNames.contains("anchors")) {
        db.createObjectStore("anchors", { keyPath: "card_id" });
      }
      if (!db.objectStoreNames.contains("server_auth")) {
        db.createObjectStore("server_auth", { keyPath: "card_id" });
      }
      // v2 (FLASH-43 pull support): cursor meta, param registry, bindings.
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains("params")) {
        db.createObjectStore("params", { keyPath: "version" });
      }
      if (!db.objectStoreNames.contains("bindings")) {
        db.createObjectStore("bindings", { keyPath: "card_id" });
      }
    };
    return new IndexedDbSyncStore(await req(openReq));
  }

  private store(name: string, mode: "readonly" | "readwrite"): IdbObjectStore {
    return this.db.transaction(name, mode).objectStore(name);
  }

  async appendEvent(e: StoredReviewEvent): Promise<void> {
    await req(this.store("events", "readwrite").put(toRow(e)));
  }

  async putEvents(events: StoredReviewEvent[]): Promise<void> {
    const tx = this.db.transaction("events", "readwrite");
    const store = tx.objectStore("events");
    for (const e of events) store.put(toRow(e));
    await new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () =>
        reject(tx.error instanceof Error ? tx.error : new Error(String(tx.error)));
    });
  }

  async deleteUnackedEvent(eventId: string): Promise<boolean> {
    const tx = this.db.transaction("events", "readwrite");
    const store = tx.objectStore("events");
    let deleted = false;
    const get = store.get(eventId) as IdbRequest<EventRow | undefined>;
    get.onsuccess = () => {
      if (!get.result || get.result.acked01 === 1) return;
      deleted = true;
      store.delete(eventId);
    };
    return new Promise<boolean>((resolve, reject) => {
      tx.oncomplete = () => resolve(deleted);
      tx.onerror = () =>
        reject(tx.error instanceof Error ? tx.error : new Error(String(tx.error)));
    });
  }

  async eventsForCard(cardId: string): Promise<StoredReviewEvent[]> {
    const rows = (await req(
      this.store("events", "readonly").index("by_card").getAll(cardId),
    )) as EventRow[];
    return rows.map(fromRow);
  }

  async unacked(limit: number): Promise<StoredReviewEvent[]> {
    const rows = (await req(
      this.store("events", "readonly").index("by_acked").getAll(0),
    )) as EventRow[];
    return rows
      .map(fromRow)
      .sort((a, b) => a.ts - b.ts)
      .slice(0, limit);
  }

  async getAnchor(cardId: string): Promise<number | undefined> {
    const row = (await req(this.store("anchors", "readonly").get(cardId))) as
      | { card_id: string; created_ts: number }
      | undefined;
    return row?.created_ts;
  }

  async setAnchor(cardId: string, createdTs: number): Promise<void> {
    // Write-once (§6.2): never overwrite a pinned anchor.
    if ((await this.getAnchor(cardId)) !== undefined) return;
    await req(this.store("anchors", "readwrite").put({ card_id: cardId, created_ts: createdTs }));
  }

  async adoptAnchor(cardId: string, createdTs: number): Promise<void> {
    // Authoritative (§6.2): the DO's frozen anchor overwrites a provisional pin.
    await req(this.store("anchors", "readwrite").put({ card_id: cardId, created_ts: createdTs }));
  }

  async getServerAuthoritative(cardId: string): Promise<CardStateSnapshot | undefined> {
    return (await req(this.store("server_auth", "readonly").get(cardId))) as
      | CardStateSnapshot
      | undefined;
  }

  async setServerAuthoritative(snapshot: CardStateSnapshot): Promise<void> {
    await req(this.store("server_auth", "readwrite").put(snapshot));
  }

  async deleteServerAuthoritative(cardId: string): Promise<void> {
    await req(this.store("server_auth", "readwrite").delete(cardId));
  }

  async getCursor(): Promise<number> {
    const row = (await req(this.store("meta", "readonly").get("pull_cursor"))) as
      | { key: string; value: number }
      | undefined;
    return row?.value ?? 0;
  }

  async setCursor(seq: number): Promise<void> {
    await req(this.store("meta", "readwrite").put({ key: "pull_cursor", value: seq }));
  }

  async putParamsRow(row: FsrsParamsRow): Promise<void> {
    await req(this.store("params", "readwrite").put(row));
  }

  async getParamsRow(version: number): Promise<FsrsParamsRow | undefined> {
    return (await req(this.store("params", "readonly").get(version))) as
      | FsrsParamsRow
      | undefined;
  }

  async putBinding(cardId: string, version: number): Promise<void> {
    await req(
      this.store("bindings", "readwrite").put({ card_id: cardId, params_version: version }),
    );
  }

  async getBinding(cardId: string): Promise<number | undefined> {
    const row = (await req(this.store("bindings", "readonly").get(cardId))) as
      | { card_id: string; params_version: number }
      | undefined;
    return row?.params_version;
  }
}
